import styled from '@emotion/styled';
import { Add, Info } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, Divider, Grid, IconButton, Stack, Switch, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { Context } from '../../../App';
import { CustomTooltip, HtmlTooltip } from '../../../utility/helpers/commonFunction';
import DisabledSlotsModal from './DisabledSlotsModal';
import DisabledSlotsTable from './DisabledSlotsTable';
import dayjs from 'dayjs';

const styles = {
    container: {
        boxShadow: 3,
        bgcolor: 'white',
        justifyContent: 'space-between',
        borderTop: 'none',
        padding: '24px',
        borderRadius: '10px',
        marginTop: '32px',
        rowGap: 3,
    },
    title: {
        fontWeight: 400,
        fontSize: '24px',
    },
    description: {
        color: '#000',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        marginTop: 1.5,
    },
    controlsContainer: {
        // borderBottom: '1px solid #0000003B',
        height: 'max-content',
        paddingBottom: '8px',
    },
    labelContainer: {
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: '160%',
        letterSpacing: '0.15px',
        // color: '#000',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    },
    infoIcon: {
        cursor: 'pointer',
        color: '#8C8C8C',
        width: '24px',
        height: '24px',
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
    },
    numberBox: {
        border: '1px solid #0000003B',
        borderRadius: '6px',
        width: '122px',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0,
        padding: '0 12px!important',
    },
    numberText: {
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        color: '#000',
    },
    labelText: {
        marginLeft: '-10px',
        fontSize: '14px',
        fontWeight: 600,
        color: '#006CDD',
        textTransform: 'uppercase',
        letterSpacing: '-0.42px',
        textAlign: 'center',
        lineHeight: '24px',
    },
    iconButton: {
        color: '#006CDD',
    },
};

const DropOffSlots = ({
    isOpenSideBar,
    isEnabled,
    maxAppointments,
    onSwitchToggle,
    onIncrement,
    onDecrement,
    isUpdating,
    dropOffFrequency,
    onFrequencyChange,
    isAllAppointmentsEnabled,
    maxAllAppointments,
    onAllAppointmentsSwitchToggle,
    onAllAppointmentsIncrement,
    onAllAppointmentsDecrement,
    slotFrequencies = [30, 60],
}) => {
    const { isOpenSideBar: contextSideBar } = useContext(Context);
    const theme = useTheme();
    const isBelow1440 = useMediaQuery(theme.breakpoints.down(1440));
    const actualSideBar = isOpenSideBar || contextSideBar;
    const [openDisabledSlotsModal, setOpenDisabledSlotsModal] = useState(false);
    const [editDate, setEditDate] = useState(null);

    const StyledToggleButtonGroupLaptop = styled(ToggleButtonGroup)(({ theme }) => ({
        borderRadius: '30px',
        padding: '6px',
        background: '#E9E9EA',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: 'max-content',

        '& .MuiToggleButton-root': {
            border: 'none',
            color: '#FFF',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.15px',
            fontFeatureSettings: "'liga' off, 'clig' off",
            borderRadius: '40px',
            padding: '0 12px',
            transition: 'none', // Disable hover effects
            textTransform: 'capitalize',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',

            '&.Mui-selected': {
                backgroundColor: '#006CDD',
                color: '#FFF',
            },

            '&:not(.Mui-selected)': {
                backgroundColor: '#E9E9EA',
                color: '#000',
            },

            // Custom hover effects for unselected buttons
            '&:not(.Mui-selected):hover': {
                backgroundColor: '#E9E9EA', // Change this color for unselected hover
            },

            // Custom hover effects for selected buttons
            '&.Mui-selected:hover': {
                backgroundColor: '#006CDD', // Change this color for selected hover
            },
        },
    }));

    return (
        <Grid container lg={12} md={12} sm={12} xs={12} sx={styles.container} alignItems="flex-start">
            <Grid item xs={12} md={12} lg={isOpenSideBar && isBelow1440 ? 12 : 4.5}>
                <Typography variant="h5" sx={styles.title}>
                    Online Booking Availability
                </Typography>
                <Typography sx={styles.description}>
                    Ensure smoother scheduling and reduce appointment overload by controlling how many customers can select the same drop-off time. when
                    requesting an appointment from your website or the Apex Auto App.
                </Typography>
            </Grid>

            <Grid container item xs={12} md={12} lg={isOpenSideBar && isBelow1440 ? 12 : 7.255}>
                <Grid item xs={12} sx={styles.controlsContainer}>
                    <Stack gap={3}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={styles.labelContainer}>Drop-Off Frequency</Typography>

                            <StyledToggleButtonGroupLaptop
                                size="small"
                                value={dropOffFrequency}
                                exclusive
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        onFrequencyChange(newValue);
                                    }
                                }}
                                aria-label="drop-off frequency"
                            >
                                {slotFrequencies.map((frequency, index) => (
                                    <ToggleButton key={index} value={frequency} aria-label={`${frequency} minutes`}>
                                        <p style={{ margin: 0 }}>Every {frequency} Minutes</p>
                                    </ToggleButton>
                                ))}
                            </StyledToggleButtonGroupLaptop>
                        </Stack>

                        <Divider />

                        {/* Limit Website & App Bookings Only - UPDATED SECTION */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ borderBottom: '1px solid #E0E0E0', paddingBottom: '8px' }}
                        >
                            <Typography sx={styles.labelContainer}>
                                Limit Website & App Bookings Only{' '}
                                <CustomTooltip
                                    width="360px"
                                    placement="top"
                                    title={
                                        <>
                                            <Typography mb={1}>Limits the number of appointments per time slot for online and app bookings only.</Typography>
                                            <Typography>
                                                Example: If a slot allows 2 bookings and both are filled online, customers won&apos;t be able to book for that
                                                time slot.
                                            </Typography>
                                        </>
                                    }
                                >
                                    <Info sx={styles.infoIcon} />
                                </CustomTooltip>
                            </Typography>

                            <Stack alignItems="flex-start" gap={0.5}>
                                <Stack direction="row" alignItems="center" gap="10px">
                                    <Box sx={styles.numberBox}>
                                        <IconButton
                                            onClick={onDecrement}
                                            disabled={!isEnabled || maxAppointments <= 1 || isUpdating}
                                            sx={{
                                                padding: '0',
                                                '& .MuiIconButton-root': {
                                                    padding: '0',
                                                },
                                            }}
                                        >
                                            <RemoveIcon
                                                sx={{
                                                    ...styles.iconButton,
                                                    opacity: !isEnabled || maxAppointments <= 1 || isUpdating ? 0.5 : 1,
                                                }}
                                            />
                                        </IconButton>
                                        <Typography
                                            sx={{
                                                ...styles.numberText,
                                                color: !isEnabled ? '#ccc' : styles.numberText.color,
                                            }}
                                        >
                                            {maxAppointments}
                                        </Typography>
                                        <IconButton
                                            onClick={onIncrement}
                                            disabled={!isEnabled || maxAppointments >= 100 || isUpdating}
                                            sx={{
                                                padding: '0',
                                                '& .MuiIconButton-root': {
                                                    padding: '0',
                                                },
                                            }}
                                        >
                                            <AddIcon
                                                sx={{
                                                    ...styles.iconButton,
                                                    opacity: !isEnabled || maxAppointments >= 100 || isUpdating ? 0.5 : 1,
                                                }}
                                            />
                                        </IconButton>
                                    </Box>

                                    <Switch
                                        color="primary"
                                        size="medium"
                                        checked={isEnabled}
                                        onChange={(e) => onSwitchToggle(e.target.checked)}
                                        disabled={isUpdating}
                                    />
                                </Stack>
                                <Typography sx={{ ...styles.labelText, opacity: !isEnabled || maxAppointments >= 100 || isUpdating ? 0.5 : 1 }}>
                                    APPTS. PER TIME SLOT
                                </Typography>
                            </Stack>
                        </Stack>

                        {/* Limit All Appointments Section */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ borderBottom: '1px solid #E0E0E0', paddingBottom: '8px' }}
                        >
                            <Typography sx={styles.labelContainer}>
                                Limit All Appointments
                                <CustomTooltip
                                    width="360px"
                                    placement="top"
                                    title={
                                        <>
                                            <Typography mb={1}>
                                                Includes both online and manual appointments toward the same time slot limit. Once full, that slot will be
                                                disabled for app and online booking.
                                            </Typography>
                                            <Typography>
                                                Example: If you book a drop-off manually for 9:00am, that time will also be hidden from online and app customers
                                                once the limit is reached.
                                            </Typography>
                                        </>
                                    }
                                >
                                    <Info sx={styles.infoIcon} />
                                </CustomTooltip>
                            </Typography>

                            <Stack alignItems="flex-start" gap={0.5}>
                                <Stack direction="row" alignItems="center" gap="10px">
                                    <Box sx={styles.numberBox}>
                                        <IconButton
                                            onClick={onAllAppointmentsDecrement}
                                            disabled={!isAllAppointmentsEnabled || maxAllAppointments <= 1 || isUpdating}
                                            sx={{
                                                padding: '0',
                                                '& .MuiIconButton-root': {
                                                    padding: '0',
                                                },
                                            }}
                                        >
                                            <RemoveIcon
                                                sx={{
                                                    ...styles.iconButton,
                                                    opacity: !isAllAppointmentsEnabled || maxAllAppointments <= 1 || isUpdating ? 0.5 : 1,
                                                }}
                                            />
                                        </IconButton>
                                        <Typography
                                            sx={{
                                                ...styles.numberText,
                                                color: !isAllAppointmentsEnabled ? '#ccc' : styles.numberText.color,
                                            }}
                                        >
                                            {maxAllAppointments}
                                        </Typography>
                                        <IconButton
                                            sx={{
                                                padding: '0',
                                                '& .MuiIconButton-root': {
                                                    padding: '0',
                                                },
                                            }}
                                            onClick={onAllAppointmentsIncrement}
                                            disabled={!isAllAppointmentsEnabled || maxAllAppointments >= 100 || isUpdating}
                                        >
                                            <AddIcon
                                                sx={{
                                                    ...styles.iconButton,
                                                    opacity: !isAllAppointmentsEnabled || maxAllAppointments >= 100 || isUpdating ? 0.5 : 1,
                                                }}
                                            />
                                        </IconButton>
                                    </Box>

                                    <Switch
                                        color="primary"
                                        size="medium"
                                        checked={isAllAppointmentsEnabled}
                                        onChange={(e) => onAllAppointmentsSwitchToggle(e.target.checked)}
                                        disabled={isUpdating}
                                    />
                                </Stack>
                                <Typography
                                    sx={{ ...styles.labelText, opacity: !isAllAppointmentsEnabled || maxAllAppointments >= 100 || isUpdating ? 0.5 : 1 }}
                                >
                                    APPTS. PER TIME SLOT
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography sx={styles.labelContainer}>Disabled Slots</Typography>

                            <Button startIcon={<Add />} variant="outlined" color="primary" onClick={() => setOpenDisabledSlotsModal(true)}>
                                Add Slot
                            </Button>
                        </Stack>

                        {/* Disabled Slots Table */}
                        <DisabledSlotsTable
                            onEdit={(item) => {
                                // Open modal with the date from the item
                                setEditDate(item.date);
                                setOpenDisabledSlotsModal(true);
                            }}
                        />
                    </Stack>
                </Grid>
            </Grid>

            {/* Disabled Slots Modal */}
            <DisabledSlotsModal
                open={openDisabledSlotsModal}
                onClose={() => {
                    setOpenDisabledSlotsModal(false);
                    setEditDate(null); // Reset edit date when closing
                }}
                dropOffFrequency={dropOffFrequency}
                startTime="08:00" // TODO: Get from API
                endTime="17:00" // TODO: Get from API
                initialDate={editDate} // Pass the date for edit mode
            />
        </Grid>
    );
};

export default DropOffSlots;
